import { useParams, Link, useNavigate } from "react-router";
import { ArrowLeft, MapPin, Wrench, Power, RefreshCw } from "lucide-react";
import {
  Body,
  Caption,
  Card,
  CardTitle,
  EmptyState,
  ErrorMessage,
  LoadingState,
  PageHeader,
  SectionTitle,
  Subtitle,
  VButton,
} from "@/components/ui";
import { useAsset, useReservationsByAsset } from "@/domains/assets/api/queries";
import { useUpdateAssetStatus } from "@/domains/assets/api/mutations";
import { AssetStatusBadge } from "@/domains/assets/components/assetStatusBadge";
import {
  AssetTypeIcon,
  assetTypeLabel,
  stripAssetTypePrefix,
} from "@/domains/assets/components/assetTypeLabel";
import type { AssetStatus } from "@/domains/assets/api/types";

export function AssetDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: asset, isPending, isError } = useAsset(id ?? "");
  const { data: reservations } = useReservationsByAsset(id ?? "");
  const updateStatus = useUpdateAssetStatus();

  if (isPending) return <LoadingState label="Asset laden…" />;
  if (isError) return <ErrorMessage message="Kon asset niet ophalen." />;
  if (!asset)
    return (
      <EmptyState
        title="Asset niet gevonden"
        action={
          <VButton variant="outline" onClick={() => navigate("/assets")}>
            Terug naar lijst
          </VButton>
        }
      />
    );

  const toggleOutOfService = (): void => {
    const next: AssetStatus = asset.status === "outOfService" ? "available" : "outOfService";
    updateStatus.mutate({ id: asset.id, status: next });
  };

  const toggleMaintenance = (): void => {
    const next: AssetStatus = asset.status === "maintenance" ? "available" : "maintenance";
    updateStatus.mutate({ id: asset.id, status: next });
  };

  return (
    <div className="space-y-6">
      <Link
        to="/assets"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden />
        Terug naar assets
      </Link>

      <PageHeader
        title={`${asset.number} — ${assetTypeLabel(asset.type)}`}
        subtitle={stripAssetTypePrefix(asset.name, asset.type)}
        actions={
          <>
            <VButton
              variant="outline"
              onClick={toggleMaintenance}
              disabled={updateStatus.isPending || asset.status === "outOfService"}
            >
              <Wrench className="size-4" aria-hidden />
              {asset.status === "maintenance" ? "Onderhoud beëindigen" : "Markeren onderhoud"}
            </VButton>
            <VButton
              variant={asset.status === "outOfService" ? "primary" : "destructive"}
              onClick={toggleOutOfService}
              disabled={updateStatus.isPending}
            >
              {asset.status === "outOfService" ? (
                <>
                  <RefreshCw className="size-4" aria-hidden />
                  Terug in dienst
                </>
              ) : (
                <>
                  <Power className="size-4" aria-hidden />
                  Buiten werking
                </>
              )}
            </VButton>
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card padding="lg" className="space-y-3 lg:col-span-2">
          <div className="flex items-center gap-2">
            <AssetTypeIcon type={asset.type} className="size-5 text-primary-700" />
            <CardTitle>Eigenschappen</CardTitle>
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div>
              <Caption tone="muted">Nummer</Caption>
              <Body weight="medium">{asset.number}</Body>
            </div>
            <div>
              <Caption tone="muted">Type</Caption>
              <Body weight="medium">{assetTypeLabel(asset.type)}</Body>
            </div>
            <div>
              <Caption tone="muted">Serienummer</Caption>
              <Body weight="medium">{asset.serialNumber}</Body>
            </div>
            <div>
              <Caption tone="muted">Status</Caption>
              <AssetStatusBadge status={asset.status} />
            </div>
            <div>
              <Caption tone="muted">Laatste check</Caption>
              <Body>{asset.lastCheck}</Body>
            </div>
            <div>
              <Caption tone="muted">Thuisbasis</Caption>
              <Body>{asset.homeBase.address}</Body>
            </div>
          </dl>
          {asset.note ? (
            <div className="rounded-md border border-warning-200 bg-warning-50 px-3 py-2">
              <Caption tone="warning">Opmerking</Caption>
              <Body tone="warning">{asset.note}</Body>
            </div>
          ) : null}
        </Card>

        <Card padding="lg" className="space-y-3">
          <CardTitle>Huidige locatie</CardTitle>
          {asset.currentLocation ? (
            <div className="flex items-start gap-2">
              <MapPin className="mt-0.5 size-4 text-primary-600" aria-hidden />
              <div>
                <Body weight="medium">{asset.currentLocation.address}</Body>
                <Caption tone="muted">{asset.currentLocation.district}</Caption>
              </div>
            </div>
          ) : (
            <Subtitle>In het depot — niet ingezet.</Subtitle>
          )}
        </Card>
      </div>

      <section className="space-y-3">
        <SectionTitle>Reservatiehistoriek</SectionTitle>
        {!reservations || reservations.length === 0 ? (
          <EmptyState title="Geen reservaties" description="Deze asset is nog niet ingepland." />
        ) : (
          <Card padding="none">
            <ul className="divide-y divide-border">
              {reservations.map((r) => (
                <li key={r.id} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <Body weight="medium">Dossier {r.caseId.replace("case-", "#")}</Body>
                    <Caption tone="muted">
                      {r.startDate} → {r.endDate}
                    </Caption>
                  </div>
                  <Link
                    to={`/cases/${r.caseId}`}
                    className="text-sm font-medium text-primary-700 hover:underline"
                  >
                    Open dossier
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </section>
    </div>
  );
}
